import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  signal,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { environment } from 'src/environments/environment';
import { SharedService } from '../../data/shared.service';
import { Router } from '@angular/router';
import { TranslatePipe } from '../../data/pipes/translate.pipe';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { FeatureAnswersAnonymousComponent } from '../../feature-answers-anonymous/feature-answers-anonymous.component';
import { updateDialogWidth } from '../../util/shared.util';

@Component({
  selector: 'app-video-background',
  imports: [CommonModule, TranslatePipe],
  templateUrl: './video-background.component.html',
  styleUrl: './video-background.component.scss',
})
export class VideoBackgroundComponent implements OnChanges, AfterViewInit {
  @Input() showLogo = true;
  @Input() showAnonymous = true;
  @Input() source = '';
  @Input() background = `linear-gradient(180deg, rgba(0, 0, 0, 0) 75%, rgba(0, 0, 0, 1) 100%),
    linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 100%)`;

  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;

  dialog = inject(Dialog);
  answersAnonymousDialogRef!: DialogRef<unknown, FeatureAnswersAnonymousComponent>;
  dialogWidth!: number;

  videoFormat = signal<'.mp4' | '.mov'>('.mp4');
  s3BaseUrl = environment.s3BaseUrl;
  fade = true;
  companyLogo = signal<string>('');
  videoFailed = false;

  @HostListener('window:resize')
  onResize(): void {
    this.dialogWidth = updateDialogWidth(this.answersAnonymousDialogRef);
  }

  get isHowdyLogo(): boolean {
    return this.companyLogo().includes('HRONWellbeing');
  }

  constructor(public sharedService: SharedService, private router: Router) {
    this.videoFormat.set(this.sharedService.getVideoFormat());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['source']) {
      this.updateVideoSource(changes['source']?.currentValue);
    }
  }

  ngAfterViewInit(): void {
    this.dialogWidth = updateDialogWidth(this.answersAnonymousDialogRef);
    this.playVideo();
    this.companyLogo.set(this.sharedService.getCompanyLogoUrl());
  }

 getHowdyLogo(): void {
    this.companyLogo.set(this.sharedService.getHowdyLogoUrl());
  }

  private async updateVideoSource(source: string): Promise<void> {
    const video = this.videoElement?.nativeElement;
    const videoSource = 'assets/videos/' + source + this.videoFormat();

    if (video) {
      const cachedResponse = await this.sharedService.getCachedVideo(videoSource);
      this.fade = false;

      if (cachedResponse) {
        const blob = await cachedResponse.blob();
        const videoUrl = URL.createObjectURL(blob);

        video.src = videoUrl;
      } else {
        video.src = `${this.s3BaseUrl}${videoSource}`;
      }
    }
  }

  goToAvailableSurveys(): void {
    this.router.navigate(['/available-surveys']);
  }

  openAnswersAnonymousDialog(): void {
    this.answersAnonymousDialogRef = this.dialog.open(FeatureAnswersAnonymousComponent, {
      disableClose: true,
      width: `${this.dialogWidth}px`
    });
  }

  onVideoError(): void {
    this.videoFailed = true;
  }

  onVideoLoaded(): void {
    this.videoFailed = false;
  }

  private playVideo(): void {
    const video = this.videoElement?.nativeElement;
    const videoSource = 'assets/videos/' + this.source + this.videoFormat();

    if (video) {
      video.src = `${this.s3BaseUrl}${videoSource}`;
      video.muted = true;
      video.play().catch(() => {
        this.videoFailed = true;
      });
    }
  }
}
